import { useEffect, useState } from 'react';
import { FORM_ERROR } from 'final-form';
import { Form } from 'react-final-form';
import { t } from 'ttag';

import { usePhoneVerification } from '@hook/usePhoneVerification';
import { PhoneVerificationModal, FormData } from '@components/PhoneVerificationModal';
import { useTimer } from '@hook/useTimer';

import { usePhoneVerificationModalQuery } from './gql/graphql.v5.generated';

// In seconds
const RESEND_SMS_INTERVAL = 60;

interface Props {
  isOpen: boolean,
  title?: string,
  description?: string,
  onVerify: () => void,
  onClose: () => void,
}

export const PhoneVerificationModalContainer: React.FC<Props> = ({
  isOpen,
  title,
  description,
  onVerify,
  onClose,
}) => {
  const [isSmsConfiramtion, setIsSmsConfiramtion] = useState(false);

  const { data } = usePhoneVerificationModalQuery({
    context: {
      v5: true,
      batch: true,
    },
  });

  const {
    sendSms,
    sendSmsLoading,
    phoneVerification,
  } = usePhoneVerification();

  const {
    timeSeconds: timeToResendSms,
    reset,
    start,
  } = useTimer({
    initialStart: false,
    intervalSeconds: 1,
    initialTime: RESEND_SMS_INTERVAL,
  });

  const handleSendSms = async (phoneNumber: string) => {
    try {
      const response = await sendSms({
        variables: {
          phoneNumber,
        },
      });

      if (response.data?.sendSms?.errors) {
        const errors = response.data?.sendSms?.errors;
        const error = errors[0].messages[0];

        return {
          [FORM_ERROR]: error.message,
        };
      }

      reset();
      return null;
    } catch {
      return {
        [FORM_ERROR]: t`Виникла помилка, повторіть спробу через декілька хвилин`,
      };
    }
  };

  const handleSubmit = async (formData: FormData) => {
    if (formData.verificationCode) {
      try {
        const response = await phoneVerification({
          variables: {
            verificationCode: formData.verificationCode,
          },
        });

        if (response.data?.phoneVerification?.errors) {
          const errors = response.data?.phoneVerification?.errors;
          const error = errors[0].messages[0];

          return {
            [FORM_ERROR]: error.message,
          };
        }
      } catch {
        return {
          [FORM_ERROR]: t`Виникла помилка, повторіть спробу через декілька хвилин`,
        };
      }

      onVerify();
    } else if (formData.phoneNumber) {
      const errors = await handleSendSms(formData.phoneNumber);

      if (!errors) {
        setIsSmsConfiramtion(true);
      } else {
        return errors;
      }
    }

    return null;
  };

  useEffect(() => {
    return () => {
      setIsSmsConfiramtion(false);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isSmsConfiramtion) {
      start();
    }
  }, [isSmsConfiramtion, start]);

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{
        phoneNumber: data?.me?.phoneNumber,
      }}
      render={() => (
        <PhoneVerificationModal
          isOpen={isOpen}
          withClose={true}
          isSmsConfiramtion={isSmsConfiramtion}
          sendSmsLoading={sendSmsLoading}
          timeToResendSms={timeToResendSms}
          title={title}
          description={description}
          onRequestClose={onClose}
          onRequestedResendSms={handleSendSms}
        />
      )}
    />
  );
};
